<script lang="ts">
	import { afterUpdate, onMount } from 'svelte';
	import { flip } from 'svelte/animate';
	import { dndzone } from 'svelte-dnd-action';
	import { SystemScreenBreakpoints } from '@Shelly/shelly-design-system/helpers/enums';
	import CameraTile from './CameraTile.svelte';
	import CameraPlaceholderTile from './CameraPlaceholderTile.svelte';
	import EmptyList from '../../Lists/EmptyList.svelte';
	import type { Device } from 'src/types/Device';
	import type { GridLayout } from '../helpers/camera-monitoring-settings';
	import { createEventDispatcher } from 'svelte';
	import { format } from 'svelte-i18n';
	import EmptyPlaceholder from 'src/components/EmptyPlaceholder.svelte';

	export let cameras: Device[] = [];
	export let cols: number = 2;
	export let layout: GridLayout = '2x2';
	export let focusedCameraId: string = '';
	export let editMode: boolean = false;
	export let onOpenSettings: () => void = () => {};

	const dispatch = createEventDispatcher();
	const flipDurationMs = 0;
	const MOSAIC_BOTTOM_GAP_PX = 16;
	const MOSAIC_MIN_HEIGHT_PX = 240;

	// Phone: keep 16:9 tile proportions and scroll. Larger viewports use the
	// viewport-fit mosaic (equal `1fr` rows filling remaining height).
	// Width 0 (pre-measure) stays non-fill so mobile doesn't flash stretched.
	let windowInnerWidth = 0;
	$: fillViewport = windowInnerWidth >= SystemScreenBreakpoints.MOBILE_MAX;

	// Focus (spotlight) layout: one large camera spanning a 2x2 area plus up to
	// 5 smaller cameras filling the rest of a 3x3 grid. Falls back to the first
	// camera when the saved focus target isn't in the current list.
	$: isFocus = layout === 'focus';
	$: focusedCamera = cameras.find((c) => c.id === focusedCameraId) ?? cameras[0] ?? null;
	$: smallCameras = focusedCamera ? cameras.filter((c) => c.id !== focusedCamera?.id).slice(0, 5) : [];
	$: focusPlaceholders = buildPlaceholders('focus', Math.max(0, 5 - smallCameras.length));

	// Uniform grids expose a full NxN of slots (2x2→4, 3x3→9, 4x4→16), growing by
	// complete rows when there are more cameras than the square. Empty slots become
	// "add camera" placeholders. Placeholders are hidden while reordering (DnD only
	// acts on real cameras).
	const SQUARE_SLOTS: Record<string, number> = { '1x1': 1, '2x2': 4, '3x3': 9, '4x4': 16 };
	$: safeCols = Math.max(cols, 1);
	$: gridSlotTarget = Math.max(
		SQUARE_SLOTS[layout] ?? safeCols * safeCols,
		Math.ceil(reorderableCameras.length / safeCols) * safeCols,
	);
	$: gridPlaceholders = editMode
		? []
		: buildPlaceholders('grid', Math.max(0, gridSlotTarget - reorderableCameras.length));
	$: gridItems = [...reorderableCameras, ...gridPlaceholders];
	$: mosaicCols = isFocus ? 3 : safeCols;
	$: mosaicRows = isFocus
		? 3
		: Math.max(1, Math.ceil((editMode ? reorderableCameras.length : gridItems.length) / safeCols));

	function buildPlaceholders(prefix: string, count: number) {
		return Array.from({ length: count }, (_, i) => ({ id: `__${prefix}-placeholder-${i}` }));
	}

	function isPlaceholderId(id: unknown) {
		return typeof id === 'string' && id.startsWith('__') && id.includes('-placeholder-');
	}

	// DS pattern: drag is disabled until card's drag handle is grabbed
	let dragDisabled = true;
	// Tracks an active drag so the external `cameras` sync below doesn't clobber the
	// DnD working list mid-drag (which would duplicate the dragged item -> duplicate keys).
	let isDragging = false;

	type ReorderableCamera = Device & { isDndShadowItem?: boolean; [key: string]: unknown };
	let reorderableCameras: ReorderableCamera[] = [];

	// Viewport-fit: size the mosaic to the remaining viewport under chrome (tabs/menu)
	// so equal `1fr` rows fill the wall without clipping the last row.
	let mosaicEl: HTMLElement | undefined;
	let windowInnerHeight = 0;
	let mosaicHeightPx = MOSAIC_MIN_HEIGHT_PX;

	function updateMosaicHeight() {
		if (!fillViewport || !mosaicEl || !windowInnerHeight) return;
		const top = mosaicEl.getBoundingClientRect().top;
		mosaicHeightPx = Math.max(
			MOSAIC_MIN_HEIGHT_PX,
			Math.floor(windowInnerHeight - top - MOSAIC_BOTTOM_GAP_PX),
		);
	}

	let resizeObserver: ResizeObserver | undefined;

	function observeMosaic(el: HTMLElement | undefined) {
		resizeObserver?.disconnect();
		if (!el || !fillViewport) return;
		resizeObserver = new ResizeObserver(() => updateMosaicHeight());
		resizeObserver.observe(el);
		updateMosaicHeight();
	}

	$: observeMosaic(fillViewport ? mosaicEl : undefined);
	$: if (fillViewport) updateMosaicHeight();

	onMount(() => {
		updateMosaicHeight();
		return () => resizeObserver?.disconnect();
	});

	afterUpdate(updateMosaicHeight);

	// Sync from `cameras`, but not mid-drag (would clobber the DnD list and duplicate items).
	$: syncFromCameras(cameras);

	function syncFromCameras(list: Device[]) {
		if (isDragging) return;
		reorderableCameras = list.map((c) => ({ ...c, isDndShadowItem: false })) as ReorderableCamera[];
	}

	function handleSort(e: CustomEvent) {
		const items = (e.detail.items as ReorderableCamera[]).filter(
			(item) => !isPlaceholderId(item.id),
		);
		reorderableCameras = items;
		handleEndDrag();
	}

	function handleFinalizeSort(e: CustomEvent) {
		const items = (e.detail.items as ReorderableCamera[]).filter(
			(item) => !isPlaceholderId(item.id),
		);
		reorderableCameras = items;
		handleEndDrag();
		isDragging = false;
		dispatch('reorder', { items: reorderableCameras });
	}

	const handleStartDrag = () => {
		dragDisabled = false;
		isDragging = true;
	};

	const handleEndDrag = () => {
		dragDisabled = true;
	};

	const transformDraggedElement = (draggedEl: HTMLElement | undefined) => {
		const child = draggedEl?.firstChild as HTMLElement;
		child?.focus();
	};
</script>

<svelte:window
	bind:innerWidth={windowInnerWidth}
	bind:innerHeight={windowInnerHeight}
	on:resize={updateMosaicHeight}
/>

{#if cameras.length === 0}
	<div class="mt-md-m mt-sm-0 text-center d-flex flex-direction-column align-items-center">
		<EmptyPlaceholder class="mt-md-l mt-sm-s mb-m" />
		<EmptyList
			title={$format('empty_camera_dashboard_label', {
				default: 'Dashboard is empty',
			})}
			description={$format('empty_camera_dashboard_descr', {
				default: 'Customize your dashboard to select which cameras to display.',
			})}
			btnText={$format('button_customize_widget', { default: 'Customize' })}
			onClick={onOpenSettings}
		/>
	</div>
{:else if isFocus}
	<div
		bind:this={mosaicEl}
		class="camera-mosaic camera-mosaic-focus"
		class:fill-viewport={fillViewport}
		class:focus-stacked={!fillViewport}
		style={fillViewport
			? `height: ${mosaicHeightPx}px; --mosaic-cols: 3; --mosaic-rows: 3;`
			: '--mosaic-cols: 3; --mosaic-rows: 3;'}
	>
		{#if focusedCamera}
			<div class="camera-mosaic-cell camera-mosaic-cell-main">
				<CameraTile camera={focusedCamera} fillContainer={fillViewport} />
			</div>
		{/if}
		<!--
			Desktop wall: `display: contents` so children participate in the 3x3 mosaic.
			Mobile (`focus-stacked`): real 2-col grid under the main camera.
		-->
		<div
			class="camera-mosaic-focus-secondary"
			style={!fillViewport
				? 'display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: var(--spacing-s); width: 100%;'
				: undefined}
		>
			{#each smallCameras as camera (camera.id)}
				<div class="camera-mosaic-cell">
					<CameraTile
						{camera}
						fillContainer={fillViewport}
						showFocusButton
						on:focus={() => dispatch('focus', { id: camera.id })}
					/>
				</div>
			{/each}
			{#each focusPlaceholders as placeholder (placeholder.id)}
				<div class="camera-mosaic-cell">
					<CameraPlaceholderTile onAddCamera={onOpenSettings} />
				</div>
			{/each}
		</div>
	</div>
{:else}
	<div
		bind:this={mosaicEl}
		class="camera-mosaic"
		class:edit-mode={editMode}
		class:fill-viewport={fillViewport}
		style={fillViewport
			? `height: ${mosaicHeightPx}px; --mosaic-cols: ${mosaicCols}; --mosaic-rows: ${mosaicRows};`
			: `--mosaic-cols: ${mosaicCols}; --mosaic-rows: ${mosaicRows};`}
		on:consider={handleSort}
		on:finalize={handleFinalizeSort}
		use:dndzone={{
			// Must stay 1:1 with rendered children (cameras then placeholders).
			items: editMode ? reorderableCameras : gridItems,
			flipDurationMs,
			dragDisabled: !editMode || dragDisabled,
			dropTargetStyle: {},
			transformDraggedElement,
			zoneTabIndex: reorderableCameras.length > 0 ? 0 : -1,
		}}
	>
		{#each reorderableCameras as camera (camera.id)}
			<div
				class="camera-mosaic-cell"
				class:dnd-placeholder={camera.isDndShadowItem}
				animate:flip={{ duration: flipDurationMs }}
			>
				{#if !camera.isDndShadowItem}
					<CameraTile
						{camera}
						{editMode}
						fillContainer={fillViewport}
						on:handle-drag-start={handleStartDrag}
						on:handle-drag-end={handleEndDrag}
					/>
				{/if}
			</div>
		{/each}
		{#each gridPlaceholders as placeholder (placeholder.id)}
			<div class="camera-mosaic-cell" animate:flip={{ duration: flipDurationMs }}>
				<CameraPlaceholderTile onAddCamera={onOpenSettings} />
			</div>
		{/each}
	</div>
{/if}

<style>
	/* Default: rows size to tile content (16:9). `.fill-viewport` switches to equal
	   `1fr` rows that fill remaining height under app chrome (desktop wall view). */
	.camera-mosaic {
		display: grid;
		grid-template-columns: repeat(var(--mosaic-cols, 2), minmax(0, 1fr));
		grid-auto-rows: auto;
		gap: var(--spacing-s);
		width: 100%;
		min-height: 0;
	}

	.camera-mosaic.fill-viewport {
		grid-template-rows: repeat(var(--mosaic-rows, 2), minmax(0, 1fr));
		grid-auto-rows: unset;
	}

	.camera-mosaic-cell {
		min-width: 0;
		min-height: 0;
		overflow: hidden;
	}

	.camera-mosaic.fill-viewport .camera-mosaic-cell {
		height: 100%;
	}

	/* Desktop focus wall: main spans 2×2 in a 3×3; secondary children rejoin via contents. */
	.camera-mosaic-focus.fill-viewport .camera-mosaic-cell-main {
		grid-column: 1 / 3;
		grid-row: 1 / 3;
	}

	.camera-mosaic-focus.fill-viewport .camera-mosaic-focus-secondary {
		display: contents;
	}

	/* Mobile focus: main on top, secondaries in a 2-up strip.
	   `:global` avoids scoped-selector mismatches on the dynamic layout classes. */
	:global(.camera-mosaic-focus.focus-stacked) {
		display: flex;
		flex-direction: column;
		height: auto;
	}

	:global(.camera-mosaic-focus.focus-stacked .camera-mosaic-cell-main) {
		width: 100%;
		height: auto;
	}

	:global(.camera-mosaic-focus.focus-stacked .camera-mosaic-focus-secondary) {
		display: grid;
		grid-template-columns: 1fr 1fr;
		gap: var(--spacing-s);
		width: 100%;
	}

	/* Hard fallback for real phone widths (covers JS/breakpoint mismatches). */
	@media (max-width: 639px) {
		:global(.camera-mosaic-focus) {
			display: flex !important;
			flex-direction: column;
			height: auto !important;
		}

		:global(.camera-mosaic-focus .camera-mosaic-cell-main) {
			grid-column: auto;
			grid-row: auto;
			width: 100%;
			height: auto;
		}

		:global(.camera-mosaic-focus .camera-mosaic-focus-secondary) {
			display: grid !important;
			grid-template-columns: 1fr 1fr !important;
			gap: var(--spacing-s);
			width: 100%;
		}

		:global(.camera-mosaic-focus .camera-mosaic-cell) {
			height: auto;
		}
	}

	/* DnD shadow cell while dragging */
	.dnd-placeholder {
		border: 2px dashed var(--color-border);
		border-radius: var(--radius-extra-big);
		background: transparent;
	}
</style>
